/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

const COLUMN_VISIBILITY_STORAGE_KEY = "data-table-column-visibility";

const getSavedColumnVisibilityByRoute = () =>
  JSON.parse(localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY) || "{}");

const getColumnVisibilityStorageScope = (pathname, tabValue) =>
  tabValue ? `${pathname}::${tabValue}` : pathname;

const getColumnHeadersSettingByRoute = (pathname, tabValue, defaultColumnHeadersSetting) => {
  const savedColumnVisibility =
    getSavedColumnVisibilityByRoute()[getColumnVisibilityStorageScope(pathname, tabValue)] || [];

  return defaultColumnHeadersSetting.map((columnHeader) => ({
    ...columnHeader,
    visible:
      savedColumnVisibility.find((savedColumn) => savedColumn.id === columnHeader.id)?.visible ??
      true,
  }));
};

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  tabValue,
  pagination,
  isSorted,
  noEndBorder,
}) {
  const { pathname } = useLocation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];
  const columns = useMemo(() => table.columns, [table.columns]);
  const data = useMemo(() => table.rows, [table.rows]);
  const defaultColumnHeadersSetting = useMemo(
    () =>
      columns.map((item) => ({
        id: item.id || item.accessor,
        name: item.Header,
        visible: true,
      })),
    [columns]
  );

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
      autoResetSortBy: false,
      autoResetPage: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) => {
    const numericValue = Number(value);
    if (!Number.isInteger(numericValue) || numericValue < 1) {
      gotoPage(0);
      return;
    }

    if (numericValue > customizedPageOptions.length) {
      gotoPage(customizedPageOptions.length - 1);
      return;
    }

    gotoPage(numericValue - 1);
  };

  // Search input value state
  const [search, setSearch] = useState(globalFilter);
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState(null);
  const [columnHeadersSetting, setColumnHeadersSetting] = useState(() =>
    getColumnHeadersSettingByRoute(pathname, tabValue, defaultColumnHeadersSetting)
  );

  useEffect(() => {
    setColumnHeadersSetting(
      getColumnHeadersSettingByRoute(pathname, tabValue, defaultColumnHeadersSetting)
    );
  }, [defaultColumnHeadersSetting, pathname, tabValue]);

  useEffect(() => {
    const storageScope = getColumnVisibilityStorageScope(pathname, tabValue);

    localStorage.setItem(
      COLUMN_VISIBILITY_STORAGE_KEY,
      JSON.stringify({
        ...getSavedColumnVisibilityByRoute(),
        [storageScope]: columnHeadersSetting.map(({ id, visible }) => ({ id, visible })),
      })
    );
  }, [columnHeadersSetting, pathname, tabValue]);

  const handleChangeColumnHeaderVisible = (columnId) => {
    setColumnHeadersSetting((prev) =>
      prev.map((columnHeader) => {
        if (columnHeader.id === columnId) {
          return {
            ...columnHeader,
            visible: !columnHeader.visible,
          };
        }
        return columnHeader;
      })
    );
  };

  const isColumnVisible = (columnId) =>
    columnHeadersSetting.find((columnHeader) => columnHeader.id === columnId)?.visible ?? true;

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  const handleOpenFilterMenu = (event) => setFilterMenuAnchorEl(event.currentTarget);
  const handleCloseFilterMenu = () => setFilterMenuAnchorEl(null);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  return (
    <MDBox>
      {entriesPerPage || canSearch ? (
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={{ xs: 2, sm: 0 }}
          p={{ xs: 2, sm: 3 }}
          width="100%"
        >
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{
                  width: "5rem",
                  "& .MuiOutlinedInput-root": {
                    minHeight: 40,
                  },
                  "& .MuiInputBase-input": {
                    paddingTop: "10px",
                    paddingBottom: "10px",
                  },
                }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;每頁筆數
              </MDTypography>
            </MDBox>
          )}
          {canSearch && (
            <MDBox
              display="flex"
              alignItems="center"
              gap="16px"
              width={{ xs: "100%", sm: "auto" }}
              ml={{ xs: 0, sm: "auto" }}
            >
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={handleOpenFilterMenu}
                sx={{
                  height: "37.5px",
                  minHeight: "37.5px",
                  mb: 0,
                  px: 2,
                  whiteSpace: "nowrap",
                }}
              >
                篩選顯示
              </MDButton>
              <Menu
                anchorEl={filterMenuAnchorEl}
                open={Boolean(filterMenuAnchorEl)}
                onClose={handleCloseFilterMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  sx: {
                    maxHeight: "min(360px, calc(100vh - 120px))",
                    overflowY: "auto",
                  },
                }}
              >
                {columnHeadersSetting.map((columnHeader) => {
                  return (
                    <MDBox
                      key={columnHeader.id}
                      sx={{ display: "flex", gap: 2, alignItems: "center" }}
                    >
                      <Checkbox
                        checked={columnHeader.visible}
                        onChange={(e) => {
                          handleChangeColumnHeaderVisible(columnHeader.id);
                        }}
                      />
                      {columnHeader.name}
                    </MDBox>
                  );
                })}
              </Menu>
              <MDBox width={{ xs: "100%", sm: "12rem" }}>
                <MDInput
                  placeholder="搜尋..."
                  value={search}
                  size="small"
                  fullWidth
                  onChange={({ currentTarget }) => {
                    setSearch(search);
                    onSearchChange(currentTarget.value);
                  }}
                />
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      ) : null}

      {isMobile ? (
        <MDBox px={2} display="flex" flexDirection="column" gap={1.5}>
          {page.map((row) => {
            prepareRow(row);
            const visibleCells = row.cells.filter((cell) => isColumnVisible(cell.column.id));

            return (
              <MDBox
                key={row.id}
                {...row.getRowProps()}
                borderRadius="lg"
                p={2}
                sx={({ borders: { borderWidth }, palette: { grey } }) => ({
                  border: `${borderWidth[1]} solid ${grey[200]}`,
                  backgroundColor: "#fff",
                })}
              >
                {visibleCells.map((cell) => (
                  <MDBox
                    key={cell.column.id}
                    {...cell.getCellProps()}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={2}
                    py={0.75}
                    sx={({ borders: { borderWidth } }) => ({
                      borderBottom:
                        cell.column.id === visibleCells[visibleCells.length - 1]?.column.id
                          ? "none"
                          : `${borderWidth[1]} solid rgba(161, 161, 161, 0.18)`,
                    })}
                  >
                    <MDTypography
                      variant="caption"
                      color="secondary"
                      fontWeight="medium"
                      sx={{ flex: "0 0 42%", wordBreak: "break-word" }}
                    >
                      {cell.column.render("Header")}
                    </MDTypography>
                    <MDBox
                      flex="1"
                      minWidth={0}
                      display="flex"
                      justifyContent="flex-end"
                      textAlign="right"
                      sx={{ wordBreak: "break-word" }}
                    >
                      <MDTypography component="div" variant="button" color="text">
                        {cell.render("Cell")}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                ))}
              </MDBox>
            );
          })}
        </MDBox>
      ) : (
        <TableContainer sx={{ boxShadow: "none", width: "100%", overflowX: "auto" }}>
          <Table {...getTableProps()}>
            <MDBox component="thead">
              {headerGroups.map((headerGroup, key) => (
                <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers
                    .filter((column) => isColumnVisible(column.id))
                    .map((column, idx) => (
                      <DataTableHeadCell
                        key={idx}
                        {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                        width={column.width ? column.width : "auto"}
                        align={column.align ? column.align : "left"}
                        sorted={setSortedValue(column)}
                      >
                        {column.render("Header")}
                      </DataTableHeadCell>
                    ))}
                </TableRow>
              ))}
            </MDBox>
            <TableBody {...getTableBodyProps()}>
              {page.map((row, key) => {
                prepareRow(row);
                return (
                  <TableRow key={key} {...row.getRowProps()}>
                    {row.cells
                      .filter((cell) => isColumnVisible(cell.column.id))
                      .map((cell, idx) => (
                        <DataTableBodyCell
                          key={idx}
                          noBorder={noEndBorder && rows.length - 1 === key}
                          align={cell.column.align ? cell.column.align : "left"}
                          {...cell.getCellProps()}
                        >
                          {cell.render("Cell")}
                        </DataTableBodyCell>
                      ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              顯示第 {entriesStart} 到 {entriesEnd} 筆，共 {rows.length} 筆資料
            </MDTypography>
          </MDBox>
        )}
        {pageOptions.length > 1 && (
          <MDPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            {canPreviousPage && (
              <MDPagination item onClick={() => previousPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination.length > 6 ? (
              <MDBox width="5rem" mx={1}>
                <MDInput
                  inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                  value={customizedPageOptions[pageIndex]}
                  onChange={handleInputPagination}
                />
              </MDBox>
            ) : (
              renderPagination
            )}
            {canNextPage && (
              <MDPagination item onClick={() => nextPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </MDBox>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  tabValue: null,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  tabValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default DataTable;
